import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser, setUser } from "../features/auth/authSlice";
import { FiLogOut, FiMenu, FiX, FiMoreVertical, FiEdit3 } from "react-icons/fi";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  MdPeople,
  MdPolicy,
  MdEventNote,
  MdAccountBalance,
  MdDashboard,
} from "react-icons/md";
import EditAccountModal from "../features/auth/editAccount";

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase/firebaseConfig";
import { auth } from "../firebase/firebaseConfig";
import { updateProfile as firebaseUpdateProfile } from "firebase/auth";
import { toast } from "react-toastify";

import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

// MUI imports
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import PolicyIcon from "@mui/icons-material/Policy";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";

import AutorenewIcon from "@mui/icons-material/Autorenew";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PeopleIcon from "@mui/icons-material/People";
import DescriptionIcon from "@mui/icons-material/Description";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Tooltip from "@mui/material/Tooltip";
import { hasBranchName } from "../utils/branchNameResolver";

const SidebarLayout = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [agentDetails, setAgentDetails] = useState({ name: "", phone: "", email: "", address: "" });
  // For MUI Menu, moreOpen is the anchorEl (null or HTMLElement)
  const [moreOpen, setMoreOpen] = useState(null);
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(user?.isPhoneUser || false);
  const [missingBranchName, setMissingBranchName] = useState(false);

  // Responsive breakpoints - updated to work with new device detection logic
  const isMobile = useMediaQuery('(max-width:768px)');
  const isTablet = useMediaQuery('(min-width:769px) and (max-width:1366px)');
  const isDesktop = useMediaQuery('(min-width:1367px)');

  // Sidebar should collapse only on phones or tablets in portrait mode
  // Tablets in landscape mode should use permanent sidebar like desktop
  const shouldCollapseSidebar = isMobile || (isTablet && window.innerHeight > window.innerWidth);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check if user is admin (phone authentication)
  useEffect(() => {
    const checkAdminStatus = async () => {
      const currentUser = auth.currentUser;
      console.log('🔍 [SidebarLayout] checkAdminStatus - currentUser:', currentUser?.uid);

      if (currentUser) {
        // Check if user has phone provider (admins use phone auth)
        const isPhoneUser = currentUser.providerData.some(p => p.providerId === 'phone');
        console.log('🔍 [SidebarLayout] isPhoneUser:', isPhoneUser);

        if (isPhoneUser) {
          // Get custom claims to verify admin role
          let idTokenResult = await currentUser.getIdTokenResult();
          console.log('🔍 [SidebarLayout] Initial claims role:', idTokenResult.claims.role);

          if (idTokenResult.claims.role !== 'admin') {
            console.log('🔍 [SidebarLayout] Admin claim not found, forcing refresh...');
            idTokenResult = await currentUser.getIdTokenResult(true);
            console.log('🔍 [SidebarLayout] Refreshed claims role:', idTokenResult.claims.role);
          }

          // Fallback: If it's a phone user, treat them as admin even if claim is missing
          const finalIsAdmin = idTokenResult.claims.role === 'admin' || isPhoneUser;
          console.log('🔍 [SidebarLayout] Setting isAdmin to:', finalIsAdmin);
          setIsAdmin(finalIsAdmin);
        } else {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged((user) => {
      console.log('🔍 [SidebarLayout] onAuthStateChanged fired. User:', user?.uid);
      checkAdminStatus();
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchAgentDetails = async () => {
      if (!user?.uid) return;
      try {
        const docRef = doc(db, "agent-details", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          console.log("✅ Agent details fetched:", docSnap.data());
          setAgentDetails(docSnap.data());
        } else {
          console.warn("⚠️ No agent-details found for user, creating default profile...");
          // Auto-create agent details if missing
          const defaultDetails = {
            name: user.displayName || "Agent",
            email: user.email || "",
            phone: user.phoneNumber || "",
            address: "",
            photoURL: user.photoURL || "",
            createdAt: new Date().toISOString()
          };

          await setDoc(docRef, defaultDetails);
          console.log("✅ Created default agent details:", defaultDetails);
          setAgentDetails(defaultDetails);
        }
      } catch (err) {
        console.error("❌ Failed to fetch agent details", err);
      }
    };

    fetchAgentDetails();
  }, [user?.uid]);

  // Check if staff is missing branch name (only for non-admin staff)
  useEffect(() => {
    const checkBranchName = async () => {
      if (!user?.uid || isAdmin) {
        setMissingBranchName(false);
        return;
      }

      try {
        const hasBranch = await hasBranchName(user.uid);
        setMissingBranchName(!hasBranch);
        if (!hasBranch) {
          console.log('⚠️ Staff missing branch name - showing warning indicator');
        }
      } catch (error) {
        console.error('Error checking branch name:', error);
        setMissingBranchName(false);
      }
    };

    checkBranchName();
  }, [user?.uid, isAdmin, agentDetails.address]);

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    navigate("/login");
    setLogoutDialogOpen(false);
  };

  const cancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    try {
      const storageRef = ref(storage, `avatars/${user.uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);

      await firebaseUpdateProfile(auth.currentUser, { photoURL });
      dispatch(setUser({ ...user, photoURL }));

      await setDoc(
        doc(db, "agent-details", user.uid),
        {
          ...agentDetails,
          photoURL,
        },
        { merge: true }
      );

      toast.success("Profile picture updated!");
    } catch (err) {
      console.error("Image upload failed", err);
      toast.error("Failed to update profile picture");
    }
  };

  return (
    <Box display="flex" sx={{ height: '100vh', overflow: 'hidden' }}>
      {/* Hamburger Menu Button for Mobile/Tablet */}
      {shouldCollapseSidebar && (
        <IconButton
          onClick={handleSidebarToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1500, // Higher than calendar (1400) to ensure it's always visible
            backgroundColor: '#fff',
            boxShadow: 2,
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <FiMenu />
        </IconButton>
      )}

      <Drawer
        variant={shouldCollapseSidebar ? "temporary" : "permanent"}
        open={shouldCollapseSidebar ? sidebarOpen : true}
        onClose={handleSidebarClose}
        sx={{
          width: shouldCollapseSidebar ? 280 : 280,
          flexShrink: 0,
          pointerEvents: isAnyModalOpen ? 'none' : 'auto',
          [`& .MuiDrawer-paper`]: {
            width: 280,
            boxSizing: 'border-box',
            pointerEvents: isAnyModalOpen ? 'none' : 'auto',
            filter: isAnyModalOpen ? 'grayscale(0.3)' : 'none',
            // Enhanced styling for mobile overlay
            ...(shouldCollapseSidebar && {
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            })
          },
          // Enhanced backdrop for mobile
          ...(shouldCollapseSidebar && {
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(2px)',
            }
          })
        }}
      >
        <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Close button for mobile/tablet */}
          {shouldCollapseSidebar && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pt: 3 }}>
              <IconButton
                onClick={handleSidebarClose}
                size="small"
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                <FiX size={18} />
              </IconButton>
            </Box>
          )}

          <Box p={2} sx={{ mb: '20px' }}>
            <Box display="flex" gap={2} alignItems="flex-start" position="relative">
              <Avatar src={user?.photoURL} alt="avatar" sx={{ flexShrink: 0 }}>
                {user?.displayName?.charAt(0) || "A"}
              </Avatar>
              <Box sx={{
                flexGrow: 1,
                minWidth: 0,
                pr: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: 40 // Consistent height with Avatar
              }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: '#757575',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {agentDetails.name || user?.displayName || "Agent"}
                </Typography>
                {(agentDetails.email || user?.email) && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 400,
                      fontSize: '0.875rem',
                      color: '#757575',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mt: 0.5
                    }}
                  >
                    {agentDetails.email || user?.email}
                  </Typography>
                )}
              </Box>
              {/* Warning icon for staff without branch name */}
              {missingBranchName && (
                <Tooltip
                  title="Please add branch name in your profile"
                  arrow
                  placement="bottom"
                >
                  <IconButton
                    size="small"
                    onClick={() => setEditOpen(true)}
                    sx={{
                      position: 'absolute',
                      right: 28,
                      top: 0,
                      color: '#DC2626',
                      '&:hover': {
                        backgroundColor: 'rgba(220, 38, 38, 0.1)',
                      }
                    }}
                  >
                    <ErrorOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                onClick={() => setEditOpen(true)}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  flexShrink: 0,
                }}
              >
                <FiEdit3 fontSize="small" />
              </IconButton>
            </Box>
          </Box>



          {/* Menu items styled to match logout container */}
          <Box sx={{ px: 2 }}>
            <List sx={{ p: 0 }}>

              {isAdmin ? (
                <>
                  <ListItemButton
                    component={NavLink}
                    to="/balance"
                    onClick={() => {
                      if (shouldCollapseSidebar) {
                        handleSidebarClose();
                      }
                    }}
                    sx={{
                      borderRadius: '8px',
                      width: '250px',
                      height: '44px',
                      padding: '8px 16px',
                      justifyContent: 'flex-start',
                      backgroundColor: 'transparent',
                      color: '#757575',
                      mb: '16px',
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 400,
                      '& .MuiListItemIcon-root': {
                        color: '#757575',
                      },
                      '&:hover': {
                        backgroundColor: '#E0EBFF',
                        color: '#fff',
                        '& .MuiListItemIcon-root': {
                          color: '#7F7F7F',
                        },
                        '& .MuiListItemText-root': {
                          color: '#7F7F7F',
                        },
                      },
                      '&.active': {
                        backgroundColor: '#004DC2',
                        color: '#fff',
                        '& .MuiListItemIcon-root': {
                          color: '#fff',
                        },
                        '& .MuiListItemText-root': {
                          color: '#fff',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit' }}><AccountBalanceWalletIcon /></ListItemIcon>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          sx={{
                            fontFamily: "'Roboto', sans-serif",
                            fontWeight: 400,
                            fontSize: '1rem',
                            color: 'inherit',
                          }}
                        >
                          Finance
                        </Typography>
                      }
                    />
                  </ListItemButton>
                  <ListItemButton
                    component={NavLink}
                    to="/renewals"
                    onClick={() => {
                      if (shouldCollapseSidebar) {
                        handleSidebarClose();
                      }
                    }}
                    sx={{
                      borderRadius: '8px',
                      width: '250px',
                      height: '44px',
                      padding: '8px 16px',
                      justifyContent: 'flex-start',
                      backgroundColor: 'transparent',
                      color: '#757575',
                      mb: '16px',
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 400,
                      '& .MuiListItemIcon-root': {
                        color: '#757575',
                      },
                      '&:hover': {
                        backgroundColor: '#E0EBFF',
                        color: '#fff',
                        '& .MuiListItemIcon-root': {
                          color: '#7F7F7F',
                        },
                        '& .MuiListItemText-root': {
                          color: '#7F7F7F',
                        },
                      },
                      '&.active': {
                        backgroundColor: '#004DC2',
                        color: '#fff',
                        '& .MuiListItemIcon-root': {
                          color: '#fff',
                        },
                        '& .MuiListItemText-root': {
                          color: '#fff',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit' }}><AutorenewIcon /></ListItemIcon>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          sx={{
                            fontFamily: "'Roboto', sans-serif",
                            fontWeight: 400,
                            fontSize: '1rem',
                            color: 'inherit',
                          }}
                        >
                          Renewals
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </>
              ) : (
                <>
                  <ListItemButton
                    component={NavLink}
                    to="/renewals"
                    onClick={() => {
                      if (shouldCollapseSidebar) {
                        handleSidebarClose();
                      }
                    }}
                    sx={{
                      borderRadius: '8px',
                      width: '250px',
                      height: '44px',
                      padding: '8px 16px',
                      justifyContent: 'flex-start',
                      backgroundColor: 'transparent',
                      color: '#757575',
                      mb: '16px',
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 400,
                      '& .MuiListItemIcon-root': {
                        color: '#757575',
                      },
                      '&:hover': {
                        backgroundColor: '#E0EBFF',
                        color: '#fff',
                        '& .MuiListItemIcon-root': {
                          color: '#7F7F7F',
                        },
                        '& .MuiListItemText-root': {
                          color: '#7F7F7F',
                        },
                      },
                      '&.active': {
                        backgroundColor: '#004DC2',
                        color: '#fff',
                        '& .MuiListItemIcon-root': {
                          color: '#fff',
                        },
                        '& .MuiListItemText-root': {
                          color: '#fff',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit' }}><AutorenewIcon /></ListItemIcon>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          sx={{
                            fontFamily: "'Roboto', sans-serif",
                            fontWeight: 400,
                            fontSize: '1rem',
                            color: 'inherit',
                          }}
                        >
                          Renewals
                        </Typography>
                      }
                    />
                  </ListItemButton>
                  <ListItemButton
                    component={NavLink}
                    to="/balance"
                    onClick={() => {
                      if (shouldCollapseSidebar) {
                        handleSidebarClose();
                      }
                    }}
                    sx={{
                      borderRadius: '8px',
                      width: '250px',
                      height: '44px',
                      padding: '8px 16px',
                      justifyContent: 'flex-start',
                      backgroundColor: 'transparent',
                      color: '#757575',
                      mb: '16px',
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 400,
                      '& .MuiListItemIcon-root': {
                        color: '#757575',
                      },
                      '&:hover': {
                        backgroundColor: '#E0EBFF',
                        color: '#fff',
                        '& .MuiListItemIcon-root': {
                          color: '#7F7F7F',
                        },
                        '& .MuiListItemText-root': {
                          color: '#7F7F7F',
                        },
                      },
                      '&.active': {
                        backgroundColor: '#004DC2',
                        color: '#fff',
                        '& .MuiListItemIcon-root': {
                          color: '#fff',
                        },
                        '& .MuiListItemText-root': {
                          color: '#fff',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit' }}><AccountBalanceWalletIcon /></ListItemIcon>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          sx={{
                            fontFamily: "'Roboto', sans-serif",
                            fontWeight: 400,
                            fontSize: '1rem',
                            color: 'inherit',
                          }}
                        >
                          Balance
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </>
              )}
              <ListItemButton
                component={NavLink}
                to="/client"
                onClick={() => {
                  if (shouldCollapseSidebar) {
                    handleSidebarClose();
                  }
                }}
                sx={{
                  borderRadius: '8px',
                  width: '250px',
                  height: '44px',
                  padding: '8px 16px',
                  justifyContent: 'flex-start',
                  backgroundColor: 'transparent',
                  color: '#757575',
                  mb: '16px',
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  '& .MuiListItemIcon-root': {
                    color: '#757575',
                  },
                  '&:hover': {
                    backgroundColor: '#E0EBFF',
                    color: '#fff',
                    '& .MuiListItemIcon-root': {
                      color: '#7F7F7F',
                    },
                    '& .MuiListItemText-root': {
                      color: '#7F7F7F',
                    },
                  },
                  '&.active': {
                    backgroundColor: '#004DC2',
                    color: '#fff',
                    '& .MuiListItemIcon-root': {
                      color: '#fff',
                    },
                    '& .MuiListItemText-root': {
                      color: '#fff',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}><PeopleIcon /></ListItemIcon>
                <ListItemText
                  disableTypography
                  primary={
                    <Typography
                      sx={{
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 400,
                        fontSize: '1rem',
                        color: 'inherit',
                      }}
                    >
                      Clients
                    </Typography>
                  }
                />
              </ListItemButton>
              <ListItemButton
                component={NavLink}
                to="/policy"
                onClick={() => {
                  if (shouldCollapseSidebar) {
                    handleSidebarClose();
                  }
                }}
                sx={{
                  borderRadius: '8px',
                  width: '250px',
                  height: '44px',
                  padding: '8px 16px',
                  justifyContent: 'flex-start',
                  backgroundColor: 'transparent',
                  color: '#757575',
                  mb: '16px',
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  '& .MuiListItemIcon-root': {
                    color: '#757575',
                  },
                  '&:hover': {
                    backgroundColor: '#E0EBFF',
                    color: '#fff',
                    '& .MuiListItemIcon-root': {
                      color: '#7F7F7F',
                    },
                    '& .MuiListItemText-root': {
                      color: '#7F7F7F',
                    },
                  },
                  '&.active': {
                    backgroundColor: '#004DC2',
                    color: '#fff',
                    '& .MuiListItemIcon-root': {
                      color: '#fff',
                    },
                    '& .MuiListItemText-root': {
                      color: '#fff',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}><DescriptionIcon /></ListItemIcon>
                <ListItemText
                  disableTypography
                  primary={
                    <Typography
                      sx={{
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 400,
                        fontSize: '1rem',
                        color: 'inherit',
                      }}
                    >
                      Policies
                    </Typography>
                  }
                />
              </ListItemButton>
              {/* Meetings Tab Removed as per request */}
              {/* Admin Dashboard tab - only show for admin users */}
              {isAdmin && (
                <ListItemButton
                  component={NavLink}
                  to="/staffs"
                  onClick={() => {
                    if (shouldCollapseSidebar) {
                      handleSidebarClose();
                    }
                  }}
                  sx={{
                    borderRadius: '8px',
                    width: '250px',
                    height: '44px',
                    padding: '8px 16px',
                    justifyContent: 'flex-start',
                    backgroundColor: 'transparent',
                    color: '#757575',
                    mb: '16px',
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 400,
                    '& .MuiListItemIcon-root': {
                      color: '#757575',
                    },
                    '&:hover': {
                      backgroundColor: '#E0EBFF',
                      color: '#fff',
                      '& .MuiListItemIcon-root': {
                        color: '#7F7F7F',
                      },
                      '& .MuiListItemText-root': {
                        color: '#7F7F7F',
                      },
                    },
                    '&.active': {
                      backgroundColor: '#004DC2',
                      color: '#fff',
                      '& .MuiListItemIcon-root': {
                        color: '#fff',
                      },
                      '& .MuiListItemText-root': {
                        color: '#fff',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit' }}><PersonIcon /></ListItemIcon>
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontWeight: 400,
                          fontSize: '1rem',
                          color: 'inherit',
                        }}
                      >
                        Staffs
                      </Typography>
                    }
                  />
                </ListItemButton>
              )}

            </List>
          </Box>

          <Box mt="auto" p={2}>
            <List sx={{ p: 0 }}>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: '8px',
                  width: '250px',
                  height: '44px',
                  padding: '8px 16px',
                  justifyContent: 'flex-start',
                  backgroundColor: 'transparent',
                  color: '#757575',
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  '& .MuiListItemIcon-root': {
                    color: '#757575',
                    minWidth: '40px',
                  },
                  '&:hover': {
                    backgroundColor: '#F04438 !important',
                    color: '#fff !important',
                    '& .MuiListItemIcon-root': {
                      color: '#fff !important',
                    },
                    '& .MuiListItemText-root': {
                      color: '#fff !important',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <FiLogOut />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  primary={
                    <Typography
                      sx={{
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 400,
                        fontSize: '1rem',
                        color: 'inherit',
                      }}
                    >
                      Log out
                    </Typography>
                  }
                />
              </ListItemButton>
            </List>
          </Box>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isMobile ? 1 : shouldCollapseSidebar ? 2 : 3,
          mt: shouldCollapseSidebar ? 7 : 0,
          width: shouldCollapseSidebar ? '100vw' : 'auto',
          minWidth: 0,
          px: isMobile ? 0.5 : shouldCollapseSidebar ? 2 : 3,
          overflowY: 'auto',
          height: '100vh',
        }}
      >
        <Outlet />
      </Box>

      {editOpen && (
        <EditAccountModal
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          isAgentEdit={true}
          initialDetails={{
            name: agentDetails.name || "",
            phone: agentDetails.phone || "",
            email: agentDetails.email || "",
            location: agentDetails.address || ""
          }}
          onSave={async (updated) => {
            try {
              const ref = doc(db, "agent-details", user.uid);
              // Map the form data to the correct field names for agent
              // Only include fields that are defined to prevent Firebase errors
              const updatedPayload = {
                name: updated.name,
                phone: updated.phone,
                address: updated.location,
                updatedAt: new Date().toISOString()
              };

              // Filter out any undefined values
              const filteredPayload = Object.fromEntries(
                Object.entries(updatedPayload).filter(([_, value]) => value !== undefined)
              );

              await setDoc(ref, filteredPayload, { merge: true });
              setAgentDetails(filteredPayload);
              setEditOpen(false);
              // Add a small delay to ensure the modal is closed before showing toast
              setTimeout(() => {
                console.log("🎉 Agent profile update success - showing toast");
                toast.success("Agent profile successfully updated", {
                  containerId: "bottom-center",
                  position: "bottom-center",
                  autoClose: 2000,
                  hideProgressBar: true,
                  closeOnClick: false,
                  pauseOnHover: false,
                  draggable: false,
                  closeButton: false,
                  style: {
                    backgroundColor: '#166434',
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 500,
                    padding: '12px 16px',
                    minHeight: '44px',
                    alignItems: 'center',
                    gap: '2px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    marginBottom: '75vh',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    maxWidth: 'none',
                    display: 'inline-flex',
                  },
                  icon: (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ color: '#FFFFFF' }}
                    >
                      <path
                        d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ),
                });
              }, 100);
            } catch (err) {
              console.error("Failed to save agent details:", err);
              toast.error("Failed to save agent details");
            }
          }}
        />
      )}

      <Dialog
        open={logoutDialogOpen}
        onClose={cancelLogout}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{
          sx: {
            width: "600px",
            height: "192px",
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            padding: "20px 32px",
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          },
        }}
      >
        {/* Message */}
        <DialogContent sx={{ padding: 0 }}>
          <Typography
            id="logout-dialog-description"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: "20px",
              lineHeight: "24px",
              color: "#212121",
              textAlign: "left",
              letterSpacing: "0.01em",
              mt: "16px",
            }}
          >
            Are you sure you want to log out?
          </Typography>
        </DialogContent>


        <DialogActions sx={{ justifyContent: "flex-end", padding: 0, marginTop: "16px", gap: "20px" }}>
          {/* Cancel Button */}
          <Button
            onClick={cancelLogout}
            variant="outlined"
            sx={{
              width: "120px",
              height: "44px",
              borderRadius: "8px",
              borderColor: "#F04438",
              color: "#F04438",
              background: "#fff",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0.01em",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#FDEAEA",
                borderColor: "#B00020",
                color: "#B00020",
              },
            }}
          >
            Cancel
          </Button>

          {/* Logout Button */}
          <Button
            onClick={confirmLogout}
            variant="contained"
            sx={{
              width: "120px",
              height: "44px",
              borderRadius: "8px",
              backgroundColor: "#F04438 !important",
              color: "#fff !important",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0.01em",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#D93C32 !important",
              },
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SidebarLayout;
